import errorIllustration from "@/assets/images/error-illustration.png";
import Icon from "@/components/Icon";

interface ErrorProps {
  message?: string;
  action?: React.ReactNode;
  className?: string;
  title?: string;
  image?: "default" | string;
  variant?: "fullPage" | "inline";
}

const defaultMessage = "Something went wrong. Unknown error.";

export default function ErrorBoundary({ message, action, className, title, image, variant = "fullPage" }: ErrorProps) {
  const getImageSrc = () => {
    if (image === "default") {
      return errorIllustration;
    }
    return image;
  };

  const imageSrc = getImageSrc();

  // Styling based on variant
  const isInline = variant === "inline";

  const containerClasses = isInline
    ? `flex flex-col items-start p-6 bg-sys-brand-surface border-2 border-sys-brand-outline rounded-lg space-y-4 ${className}`
    : `flex flex-col justify-center items-center p-6 bg-sys-brand-surface rounded-xl space-y-6 ${className}`;

  const titleClasses = isInline
    ? "text-title-large-bold font-serif"
    : "text-headline-large-bold font-serif text-center";

  const messageClasses = isInline ? "text-body-medium" : "text-body-large text-center";

  const imageClasses = isInline ? "w-32 object-contain self-center" : "w-520 object-contain";

  // For inline variant, never show images to keep it compact
  const shouldShowImage = !isInline && imageSrc;
  const finalImageSrc = imageSrc;

  return (
    <div className={containerClasses}>
      {!!title && (
        <div className={isInline ? "flex items-start gap-3" : ""}>
          {isInline && (
            <div className="w-6 h-6 rounded-full bg-sys-brand-error flex items-center justify-center flex-shrink-0">
              <Icon name="priority_high" size={16} className="text-sys-brand-on-error" />
            </div>
          )}
          <h1 className={titleClasses}>{title}</h1>
        </div>
      )}
      <p className={messageClasses}>
        {isInline ? "" : "Error: "}
        {message || defaultMessage}
      </p>
      {!!action && action}
      {shouldShowImage && finalImageSrc && (
        <img src={finalImageSrc} alt="Error illustration" className={imageClasses} />
      )}
    </div>
  );
}
